import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const TierThresholdConfig = ({ thresholds, onUpdate, loading }) => {
  const [editMode, setEditMode] = useState(false);
  const [localThresholds, setLocalThresholds] = useState(thresholds);
  const [saving, setSaving] = useState(false);

  const handleChange = (tier, value) => {
    setLocalThresholds({
      ...localThresholds,
      [tier]: parseFloat(value) || 0
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(localThresholds);
    setSaving(false);
    setEditMode(false);
  };

  const handleCancel = () => {
    setLocalThresholds(thresholds);
    setEditMode(false);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return 'var(--color-primary)';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'bronze':
        return 'Award';
      case 'silver':
        return 'Medal';
      case 'gold':
        return 'Trophy';
      case 'platinum':
        return 'Crown';
      default:
        return 'Star';
    }
  };

  const tiers = ['bronze', 'silver', 'gold', 'platinum'];

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Settings" size={24} color="var(--color-primary)" />
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">Tier Thresholds</h3>
        </div>
        {!editMode && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditMode(true)}
            disabled={loading}
          >
            <Icon name="Edit" size={16} />
            <span className="ml-2">Edit</span>
          </Button>
        )}
      </div>
      <div className="space-y-4">
        {tiers?.map((tier) => (
          <div key={tier} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${getTierColor(tier)}20` }}
            >
              <Icon name={getTierIcon(tier)} size={20} color={getTierColor(tier)} />
            </div>
            <div className="flex-1">
              <p className="font-caption font-semibold text-foreground capitalize">{tier} Tier</p>
              {editMode ? (
                <Input
                  type="number"
                  value={localThresholds?.[tier] || 0}
                  onChange={(e) => handleChange(tier, e?.target?.value)}
                  placeholder="Enter threshold"
                  className="mt-2"
                />
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  Minimum Revenue: ${(localThresholds?.[tier] || 0)?.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {editMode && (
        <div className="flex items-center gap-3 mt-6">
          <Button
            variant="default"
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
          >
            {saving ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              <>
                <Icon name="Check" size={16} />
                <span className="ml-2">Save Changes</span>
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default TierThresholdConfig;